import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormGroup, Label, Button } from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
import {
  CircularProgress,
  Snackbar,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OrganizationalTreeSelector from "./OrganizationalTreeSelector";

import {
  getOrganizationalUnits,
  clearFetchOrganizationalUnitsError,
} from "../../store/multiAccountOnboard/multiAccountSlice";
import {
  getReleaseDetailsRequest,
  clearAllReleaseMessages,
} from "../../store/actions";

const MultipleAccountsForm = forwardRef(
  ({ organizations = [], closeModal, onAccountsAdded, onStepChange }, ref) => {
    const [selectedAccount, setSelectedAccount] = useState("");
    const [showInfo, setShowInfo] = useState(true);
    const [accountActivityStatus, setAccountActivityStatus] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const treeSelectorRef = useRef(null);
    const pendingRequestIdRef = useRef(null);
    const openedUrlsRef = useRef(new Set());
    const dispatch = useDispatch();

    const {
      isFetchingOrganizationalUnits,
      organizationalUnits,
      fetchOrganizationalUnitsError,
    } = useSelector((state) => state.organizationalUnits);

    const { releaseDetails } = useSelector((state) => state.releaseNotes);

    const resourceExplorerGuideLink =
      process.env.REACT_APP_RESOURCE_EXPLORER_MANUAL_GUIDE;

    const handleAccountChange = (e) => {
      setSelectedAccount(e.target.value);
    };

    const handleRetrieveClick = () => {
      if (selectedAccount) {
        dispatch(getOrganizationalUnits(selectedAccount));
      }
    };

    const handleSnackbarClose = () => {
      dispatch(clearFetchOrganizationalUnitsError());
    };

    const handleAccountActivityStatusChange = (status) => {
      setAccountActivityStatus(status);
    };

    // Track step and notify parent
    useEffect(() => {
      if (onStepChange) {
        onStepChange(currentStep);
      }
    }, [currentStep, onStepChange]);

    // Detect Event Insight mode to switch to step 2
    const handleEventInsightModeChange = (isActive) => {
      setCurrentStep(isActive ? 2 : 1);
    };

    useEffect(() => {
      return () => {
        if (
          treeSelectorRef.current &&
          typeof treeSelectorRef.current.stopPolling === "function"
        ) {
          treeSelectorRef.current.stopPolling();
        }
        // Don't clear organizational units data when component unmounts
        // This preserves the tree data for Event Insight mode
      };
    }, [dispatch]);

    // Handle opening Resource Explorer guide
    useEffect(() => {
      if (releaseDetails && pendingRequestIdRef.current && typeof releaseDetails === 'string') {
        // Check if we've already opened this exact URL
        if (!openedUrlsRef.current.has(releaseDetails)) {
          openedUrlsRef.current.add(releaseDetails);
          const urlToOpen = releaseDetails;
          // Clear the pending request
          pendingRequestIdRef.current = null;
          // Clear release details from Redux
          dispatch(clearAllReleaseMessages());
          // Open the document
          window.open(urlToOpen, "_blank", "noopener,noreferrer");
          // Remove from set after a delay to allow reopening if user clicks again
          setTimeout(() => {
            openedUrlsRef.current.delete(urlToOpen);
          }, 2000);
        } else {
          // URL already opened, just clear the state
          pendingRequestIdRef.current = null;
          dispatch(clearAllReleaseMessages());
        }
      }
    }, [releaseDetails, dispatch]);

    // Find the selected account details
    const selectedAccountDetails = organizations.find(
      (org) => org.accountNumber === selectedAccount
    );

    // Expose the treeSelectorRef to the parent component
    useImperativeHandle(ref, () => ({
      treeSelectorRef,
    }));

    return (
      <div className="p-3">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h5" fontWeight={600}>
            Select your master account
          </Typography>
          <Tooltip title="View pre-requisites">
            <IconButton
              size="small"
              onClick={() => setShowInfo((prev) => !prev)}
            >
              <InfoOutlinedIcon color="primary" />
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={showInfo}>
          <Box
            p={2}
            mb={3}
            borderRadius={4}
            sx={{ backgroundColor: "#f4f4f4", border: "1px solid #ddd" }}
          >
            <Typography variant="body1" fontWeight={600} gutterBottom>
              Before you proceed, please make sure:
            </Typography>
            <ul
              style={{ paddingLeft: "1.25rem", margin: 0, fontSize: "0.9rem" }}
            >
              <li>
                You have an <strong>AWS Organization</strong> already set up. If
                unsure, refer to{" "}
                <a
                  href="https://docs.aws.amazon.com/organizations/latest/userguide/orgs_introduction.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#F98125", fontWeight: 500 }}
                >
                  this AWS guide
                </a>
                .
              </li>
              <li>
                Your AWS user has permissions to create a{" "}
                <strong>Stack Set</strong> and deploy IAM roles in member
                accounts.
              </li>
              <li>
                All selected accounts must have the selected AWS Region{" "}
                <strong>enabled</strong>. If unsure, it's safe to choose{" "}
                <code style={{ color: "#1976D2" }}>us-east-1</code>.
              </li>
              <li>
                Make sure you're logged into the AWS Console with the same
                region you selected in the{" "}
                <span style={{ fontWeight: 500, color: "#1976D2" }}>
                  “Select AWS Region”
                </span>{" "}
                dropdown below.
              </li>
              <li>
                No selected account should already have a role named{" "}
                <span style={{ color: "#1976D2" }}>
                  CloudCatcherCrossAccountTrustRole
                </span>
                .
              </li>
              <li>
                No selected account should already contain a stack set named{" "}
                <span style={{ color: "#1976D2" }}>
                  CloudCatcherTrustRoleStackSet
                </span>
                .
              </li>
              <li>
                Ensure <strong>Resource Explorer</strong> is set up for each selected account. If not,{" "}
                <a
                  href="https://resource-explorer.console.aws.amazon.com/resource-explorer/home?region=us-east-1#/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976D2", fontWeight: 500 }}
                >
                  click here to enable it
                </a>
                . For detailed guidance,{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (resourceExplorerGuideLink) {
                      // Clear any existing release details first to prevent stale opens
                      dispatch(clearAllReleaseMessages());
                      // Generate a unique request ID for this click
                      pendingRequestIdRef.current = Date.now();
                      dispatch(getReleaseDetailsRequest(resourceExplorerGuideLink));
                    }
                  }}
                  style={{ color: "#1976D2", fontWeight: 500, cursor: "pointer" }}
                >
                  refer to the setup guide
                </a>
                .
              </li>
            </ul>
          </Box>
        </Collapse>

        <AvForm>
          <FormGroup>
            <Label for="masterAccount">Master Account</Label>
            <AvField
              type="select"
              name="masterAccount"
              id="masterAccount"
              className="form-control"
              onChange={handleAccountChange}
              value={selectedAccount}
            >
              <option value="">Select an account</option>
              {organizations.map((account) => (
                <option
                  key={account.accountNumber}
                  value={account.accountNumber}
                >
                  {account.accountName} ({account.accountNumber})
                </option>
              ))}
            </AvField>
          </FormGroup>

          {selectedAccount && (
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <Button 
                color="primary" 
                onClick={handleRetrieveClick}
                disabled={accountActivityStatus === "COMPLETE"}
                style={{
                  opacity: accountActivityStatus === "COMPLETE" ? 0.5 : 1,
                  cursor: accountActivityStatus === "COMPLETE" ? "not-allowed" : "pointer"
                }}
              >
                Retrieve Organization
              </Button>
              {isFetchingOrganizationalUnits && <CircularProgress size={20} />}
            </Box>
          )}
        </AvForm>

        <Snackbar
          open={!!fetchOrganizationalUnitsError}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={fetchOrganizationalUnitsError || undefined}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />

        {organizationalUnits.organizationalUnits?.length > 0 &&
          selectedAccountDetails && (
            <OrganizationalTreeSelector
              ref={treeSelectorRef}
              organizationalUnits={organizationalUnits.organizationalUnits}
              masterAccountDetails={{
                accountNumber: selectedAccountDetails.accountNumber,
                accountName: selectedAccountDetails.accountName,
                externalId: selectedAccountDetails.externalId || "",
                roleSessionName: selectedAccountDetails.iamrole || "",
                stackName: "CloudCatcherTrustRoleStackSet",
              }}
              closeModal={closeModal}
              onAccountsAdded={onAccountsAdded}
              onAccountActivityStatusChange={handleAccountActivityStatusChange}
              onEventInsightModeChange={handleEventInsightModeChange}
              organizations={organizations}
            />
          )}
      </div>
    );
  }
);

// Static method to get step information for Multiple Accounts
MultipleAccountsForm.getStepInformation = (step) => {
  switch (step) {
    case 1:
      return {
        title: "Onboard Linked Accounts",
        steps: [
          "Before beginning this step, log in to the AWS Management Console and verify that you have the necessary permissions to create an IAM role.",
          "Ensure you are logged into the Master account on the AWS Management Console.",
          "Select the master account onboarded and click Retrieve Organization.",
          "Once retrieved, select the linked/child accounts to onboard.",
          "Select the AWS region for which you will be creating resources in for the accounts.",
          "Click Generate YAML.",
          "Click Launch YAML and you will be redirected to the CloudFormation service in the Master account on AWS console.",
          "  a. Enter the stack name. Example: CloudCatcherCrossRoleStack",
          "  b. Stack Failure Options:",
          "    i. Under \"Behavior on Provisioning Failure\" select \"Roll back all stack resources\".",
          "    ii. Under \"Delete Newly Created Resources During a Rollback\" select \"Delete all newly created resources\".",
          "  c. Click Create Stack.",
          "  d. Wait for the stack to be created.",
          "Once the stack has been created and complete progress, come back to the CloudCatcher application. Click Check Status and Click Yes.",
          "Once successful status, click Check Account Status.",
          "This step takes about a minute, so give it time after clicking Check Again! Button.",
          "A successful message will pop up after clicking once more. You will now be prompted to enable Cloud Event IF you would like to do so.",
          "Note: The organization trail will be created in the master account."
        ]
      };
    case 2:
      return {
        title: "Optional: Enable Cloud Event for Linked Accounts",
        steps: [
          "If you would like to enable Cloud Event for the master account and linked accounts selected, click Yes.",
          "Ensure you are logged into the Master account on the AWS Management Console.",
          "Click Generate YAML then click Launch YAML. You will be redirected to the CloudFormation service in the Master account on AWS console.",
          "I. Enter Stack Name:",
          "  A. Navigate to the \"Provide a stack name\" section.",
          "  B. Click on the text box indicated by the first red arrow.",
          "  C. Enter your desired stack name. Ensure that it includes only letters (A-Z, a-z), numbers (0-9), and dashes (-).",
          "  D. Example: CloudEventCloudCatcherStack",
          "II. Set Parameters: Scroll down to the \"Parameters\" section.",
          "  A. Locate the \"CloudTrailName\" field and enter the desired name for the CloudTrail.",
          "  B. Locate the \"CloudtrailS3BucketName\" field and enter the desired name for the S3 bucket.",
          "  C. Keep the remaining fields set to their default values.",
          "III. Select IAM Role: (Optional)",
          "  A. Locate the \"IAM role - optional\" section.",
          "  B. Click on the dropdown menu indicated by the first red arrow.",
          "  C. Select the desired IAM role for CloudFormation operations from the dropdown list.",
          "IV. Stack Failure Options:",
          "  A. Scroll down to the \"Stack Failure Options\" section.",
          "  B. Under \"Behavior on Provisioning Failure\" select \"Roll back all stack resources\".",
          "  C. Under \"Delete Newly Created Resources During a Rollback\" select \"Delete all newly created resources\".",
          "V. Acknowledge IAM Capabilities:",
          "  A. Scroll down to the \"Capabilities\" section.",
          "  B. Check the box next to \"I acknowledge that AWS CloudFormation might create IAM resources with custom names\" as indicated by the second red arrow.",
          "VI. Create Stack:",
          "  A. After completing the above steps, click the Submit button at the bottom right corner to proceed with the stack creation process.",
          "VII. Once the stack has been successfully created, navigate to the Outputs tab of the stack.",
          "  A. Come back to this screen on CloudCatcher.",
          "  B. Configure CloudEvent by verifying the Bucket Policy for the account.",
          "    1. Copy and paste the values in the Outputs tab of the stack, of the Replication IAM Role Arn, S3Bucket Arn, and select the S3 AWS Region from the drop down.",
          "  C. Click Verify.",
          "  D. Once verified, a success message that cloud event was enabled will show.",
          "  E. You will be exited out of this window and returned to the Configuration page."
        ]
      };
    default:
      return { title: "", steps: [] };
  }
};

export default MultipleAccountsForm;
